import { z } from "zod";

let zones: string[] | undefined;
let zoneSet: Set<string> | undefined;

type IntlWithSupportedValues = typeof Intl & {
  supportedValuesOf(key: "timeZone"): string[];
};

/**
 * IANA zone names the runtime supports, plus "UTC": browsers report it from
 * `resolvedOptions().timeZone`, but `supportedValuesOf` leaves it out.
 */
export function listTimeZones(): string[] {
  if (!zones) {
    const supported = new Set(
      (Intl as IntlWithSupportedValues).supportedValuesOf("timeZone")
    );
    supported.add("UTC");
    zones = [...supported].sort();
    zoneSet = supported;
  }
  return zones;
}

const AREA_LOCATION = /^[A-Z][A-Za-z]*(\/[A-Z][A-Za-z0-9_+-]*)+$/;

/**
 * Runtimes disagree on spellings (Node lists `Asia/Calcutta`, Firefox reports
 * `Asia/Kolkata`), so a name off the list still counts when it has the
 * Area/Location shape and the Intl API resolves it. Abbreviations and raw
 * offsets fail the shape check.
 */
export function isValidTimeZone(value: unknown): value is string {
  if (typeof value !== "string") return false;
  listTimeZones();
  if (zoneSet!.has(value)) return true;
  if (!AREA_LOCATION.test(value)) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * Intl matches zone names case-insensitively but the agent's `ZoneInfo` does
 * not, so store the runtime's spelling (`America/NEW_YORK` → `America/New_York`).
 */
export function canonicalTimeZone(value: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: value }).resolvedOptions()
    .timeZone;
}

/** The zone the browser runs in, or null when it is not one we accept. */
export function browserTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(zone) ? zone : null;
  } catch {
    return null;
  }
}

export const TIME_ZONE_ERROR = "Time zone must be a valid IANA name";

export const timeZoneSchema = z
  .string()
  .refine(isValidTimeZone, { message: TIME_ZONE_ERROR })
  .transform(canonicalTimeZone);
